/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Paper,
  Snackbar,
  StyleRules,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import {Delete, Edit, ExitToApp} from "@material-ui/icons";
import {Alert} from "@material-ui/lab";
import classNames from 'classnames';
import React, {memo, useEffect, useState} from 'react';
import {Trans, useTranslation} from "react-i18next";
import {apiUrl} from "../../utils/api";
import GalleriesAdmin from "./GalleriesAdmin";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type AdminClassKey =
  'root' | 'centered' | 'loginForm' | 'loginField' | 'header' | 'onDarkButton' | 'adminTabs' | 'tableContainer' | 'commentCell';

const useStyles = makeStyles<Theme, {}, AdminClassKey>(
  (): StyleRules<AdminClassKey> =>
    createStyles({
      root: {
        width: '80%',
        marginLeft: '10%',
        paddingTop: '40px',
        paddingBottom: '60px'
      },
      centered: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '80px'
      },
      loginForm: {
        background: 'white',
        padding: '24px 32px',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        width: '320px'
      },
      loginField: {
        margin: '12px 0'
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      },
      onDarkButton: {
        color: 'var(--color-text)',
        borderColor: 'var(--color-border)'
      },
      adminTabs: {
        marginBottom: '24px',
        '& .MuiTab-textColorPrimary': {
          color: 'var(--color-text-muted)'
        },
        '& .MuiTab-textColorPrimary.Mui-selected': {
          color: 'var(--color-text-strong)'
        }
      },
      tableContainer: {
        marginBottom: '20px'
      },
      commentCell: {
        maxWidth: '360px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }
    }),
  {
    name: 'Admin'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Types
//----------------------------------------------------------------------------------------------------------------------
type CommentRow = {
  id: number;
  name: string;
  email: string;
  comment: string;
  date: string;
};

type SnackState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type AdminProps = {
  className?: string;
};

export default memo(({className}: AdminProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');

  const [checkingSession, setCheckingSession] = useState<boolean>(true);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [loggingIn, setLoggingIn] = useState<boolean>(false);

  const [adminTab, setAdminTab] = useState<number>(0);

  const [rows, setRows] = useState<CommentRow[]>([]);
  const [loadingRows, setLoadingRows] = useState<boolean>(false);

  const [editRow, setEditRow] = useState<CommentRow | null>(null);
  const [savingEdit, setSavingEdit] = useState<boolean>(false);
  const [deleteRow, setDeleteRow] = useState<CommentRow | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [snack, setSnack] = useState<SnackState>({open: false, message: '', severity: 'success'});
  const closeSnack = () => setSnack((s) => ({...s, open: false}));

  const handleAdminTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setAdminTab(newValue);
    closeSnack();
  };

  const loadComments = () => {
    setLoadingRows(true);
    fetch(apiUrl('api/admin/listComments.php'), {credentials: 'include'})
    .then(async (response) => {
      if (response.status === 401) {
        setAuthenticated(false);
        return;
      }
      const d = await response.json();
      if (Array.isArray(d)) {
        setRows(d);
      } else {
        setRows([]);
        setSnack({open: true, message: d.error || t('admin.comments.loadError'), severity: 'error'});
      }
    })
    .catch(() => setSnack({open: true, message: t('admin.comments.loadError'), severity: 'error'}))
    .finally(() => setLoadingRows(false));
  };

  useEffect(() => {
    fetch(apiUrl('api/admin/checkSession.php'), {credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      setAuthenticated(!!d.authenticated);
      if (d.authenticated) {
        loadComments();
      }
    })
    .catch(() => setAuthenticated(false))
    .finally(() => setCheckingSession(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = () => {
    if (password.length === 0 || loggingIn) {
      return;
    }
    setLoggingIn(true);
    setLoginError('');

    const formData = new FormData();
    formData.append('password', password);

    fetch(apiUrl('api/admin/login.php'), {method: 'post', body: formData, credentials: 'include'})
    .then(async (response) => {
      const d = await response.json();
      if (response.ok && d.success) {
        setAuthenticated(true);
        setPassword('');
        loadComments();
      } else {
        setLoginError(t('admin.login.incorrectPassword'));
      }
    })
    .catch(() => setLoginError(t('admin.login.loginFailed')))
    .finally(() => setLoggingIn(false));
  };

  const handleLogout = () => {
    fetch(apiUrl('api/admin/logout.php'), {method: 'post', credentials: 'include'})
    .finally(() => {
      setAuthenticated(false);
      setRows([]);
    });
  };

  const handleSaveEdit = () => {
    if (!editRow || savingEdit) {
      return;
    }
    setSavingEdit(true);

    const formData = new FormData();
    formData.append('id', String(editRow.id));
    formData.append('name', editRow.name);
    formData.append('email', editRow.email);
    formData.append('comment', editRow.comment);

    fetch(apiUrl('api/admin/updateComment.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        setSnack({open: true, message: t('admin.comments.updateSuccess'), severity: 'success'});
        setEditRow(null);
        loadComments();
      } else {
        setSnack({open: true, message: d.error || t('admin.comments.updateError'), severity: 'error'});
      }
    })
    .catch(() => setSnack({open: true, message: t('admin.comments.updateError'), severity: 'error'}))
    .finally(() => setSavingEdit(false));
  };

  const handleConfirmDelete = () => {
    if (!deleteRow || deleting) {
      return;
    }
    setDeleting(true);

    const formData = new FormData();
    formData.append('id', String(deleteRow.id));

    fetch(apiUrl('api/admin/deleteComment.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        setSnack({open: true, message: t('admin.comments.deleteSuccess'), severity: 'success'});
        setDeleteRow(null);
        loadComments();
      } else {
        setSnack({open: true, message: d.error || t('admin.comments.deleteError'), severity: 'error'});
      }
    })
    .catch(() => setSnack({open: true, message: t('admin.comments.deleteError'), severity: 'error'}))
    .finally(() => setDeleting(false));
  };

  if (checkingSession) {
    return (
      <div className={classNames(classes.root, classes.centered, className)}>
        <CircularProgress />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className={classNames(classes.root, classes.centered, className)}>
        <FormControl className={classes.loginForm}>
          <Typography variant="h6">{t('admin.login.title')}</Typography>
          <TextField
            className={classes.loginField}
            label={t('admin.login.password')}
            type="password"
            variant="outlined"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleLogin();
              }
            }}
            error={!!loginError}
            helperText={loginError}
          />
          <Button variant="outlined" onClick={handleLogin} disabled={password.length === 0 || loggingIn}>
            {loggingIn ? <CircularProgress size={20} /> : t('admin.login.logIn')}
          </Button>
        </FormControl>
      </div>
    );
  }

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.header}>
        <Typography variant="h5">{t('admin.title')}</Typography>
        <Button
          variant="outlined"
          className={classes.onDarkButton}
          onClick={handleLogout}
          startIcon={<ExitToApp />}
        >
          {t('admin.logOut')}
        </Button>
      </div>

      <Tabs
        value={adminTab}
        onChange={handleAdminTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.adminTabs}
      >
        <Tab label={t('admin.tabs.guestBook')} />
        <Tab label={t('admin.tabs.galleries')} />
      </Tabs>

      {adminTab === 1 ? (
        <GalleriesAdmin onUnauthenticated={() => setAuthenticated(false)} />
      ) : (
      <>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.comments.table.name')}</TableCell>
              <TableCell>{t('admin.comments.table.email')}</TableCell>
              <TableCell>{t('admin.comments.table.comment')}</TableCell>
              <TableCell>{t('admin.comments.table.date')}</TableCell>
              <TableCell align="right">{t('admin.comments.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell className={classes.commentCell}>{row.comment}</TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="edit" onClick={() => setEditRow(row)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="delete" onClick={() => setDeleteRow(row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loadingRows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">{t('admin.comments.noComments')}</TableCell>
              </TableRow>
            )}
            {loadingRows && (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('admin.comments.editDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t('admin.comments.table.name')}
            value={editRow?.name || ''}
            onChange={(e) => setEditRow((r) => r && {...r, name: e.target.value})}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('admin.comments.table.email')}
            value={editRow?.email || ''}
            onChange={(e) => setEditRow((r) => r && {...r, email: e.target.value})}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('admin.comments.table.comment')}
            multiline
            rows={4}
            value={editRow?.comment || ''}
            onChange={(e) => setEditRow((r) => r && {...r, comment: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>{t('admin.common.cancel')}</Button>
          <Button
            color="primary"
            onClick={handleSaveEdit}
            disabled={savingEdit || !editRow?.name || !editRow?.comment}
          >
            {savingEdit ? <CircularProgress size={20} /> : t('admin.common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)}>
        <DialogTitle>{t('admin.comments.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans i18nKey="admin.comments.deleteDialog.body" values={{name: deleteRow?.name}} components={{strong: <strong />}} />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)}>{t('admin.common.cancel')}</Button>
          <Button color="secondary" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert onClose={closeSnack} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
      </>
      )}
    </div>
  );
});
